import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import {
  useGroupCheckoutRefresh,
  useStoreCheckoutRefresh,
} from '../../hooks/useGroupCheckoutRefresh';
import { formatMinutes, formatMoney, formatRate } from '../../shared/plan';
import {
  AllowanceMeter,
  AllowanceMeterSkeleton,
  type Bar,
  Bars,
  BarsSkeleton,
  Block,
  BlockSkeleton,
  Breakdown,
  BreakdownSkeleton,
  Button,
  Callout,
  EmptyState,
  FiguresSummary,
  type GateQuery,
  IconArrowRight,
  IconExternal,
  IconReceipt,
  Page,
  Pagination,
  PeriodNav,
  PlanName,
  QueryGate,
  Reason,
  ReceiptLines,
  ReceiptSkeleton,
  ReceiptTotal,
  Row,
  SectionHead,
  Segmented,
  Select,
  Spinner,
  StatusLabel,
  SummaryFigure,
  SummaryFigureSkeleton,
  Surface,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeletonRows,
  Tag,
  hueFor,
} from '../../ui/v2';
import {
  type BillingScope,
  toGate,
  useBillingPeriods,
  useInvoices,
  usePortal,
  useSubscription,
  useUsageHistory,
  useUsageSummary,
} from './data';
import {
  BILLING_PATH,
  type BillingPeriod,
  GROUP_RANGES,
  type GroupUsageSummary,
  INVOICES_PER_PAGE,
  type InvoicePage,
  PLAN_TAG,
  RANGE_LABEL,
  type Range,
  STORE_RANGES,
  type Subscription,
  type UsageSummary,
  formatDate,
  groupBillingPath,
  groupChargeLines,
  invoicePeriodLabel,
  invoiceWord,
  isNotStarted,
  periodLabel,
  storeChargeLines,
  subscriptionAddonRows,
  subscriptionWord,
} from './model';

/**
 * Billing V2 — one page for a store and for a store group (`scope`).
 *
 * Reading order: what this period costs and how much of the allowance is
 * used (figures + meter) → the shape of usage over time (bars) → the plan
 * and the charges it produces, side by side → invoices. Everything that
 * writes goes to Stripe (portal) or to the Plans page; this page reads.
 *
 * Fail closed: store-level plan and payment controls need the
 * authoritative user info *and* a usage summary that says the store is
 * billed on its own (`billing_managed_at_group_level === false`).
 */
const BillingPage: React.FC<{ scope?: 'store' | 'group' }> = ({
  scope = 'store',
}) => (scope === 'group' ? <GroupBilling /> : <StoreBilling />);

export default BillingPage;

const STORE_SCOPE: BillingScope = { kind: 'store' };
const tzWords = (tz: string) => tz.replace(/_/g, ' ');

// ---------------------------------------------------------------------------
// URL state shared by both scopes
// ---------------------------------------------------------------------------

function useBillingParams(periods: BillingPeriod[], ranges: Range[]) {
  const [params, setParams] = useSearchParams();
  const set = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) =>
            v == null ? next.delete(k) : next.set(k, v),
          );
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const defaultIdx = Math.max(
    0,
    periods.findIndex((p) => p.is_current),
  );
  const asked = params.get('period');
  const askedIdx = asked
    ? periods.findIndex((p) => p.period_start === asked)
    : -1;
  const periodIdx = askedIdx >= 0 ? askedIdx : defaultIdx;
  const rangeParam = params.get('range') as Range | null;
  const range: Range =
    rangeParam && ranges.includes(rangeParam) ? rangeParam : ranges[0];
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1);
  return {
    set,
    period: periods[periodIdx] as BillingPeriod | undefined,
    periodIdx,
    defaultIdx,
    range,
    page,
    updated: params.get('updated') === '1',
    setPeriod: (i: number) =>
      set({
        period: i === defaultIdx ? null : (periods[i]?.period_start ?? null),
        range: null,
      }),
    setRange: (r: Range) => set({ range: r === ranges[0] ? null : r }),
    setPage: (p: number) => set({ page: p === 1 ? null : String(p) }),
  };
}

// ---------------------------------------------------------------------------
// Store scope
// ---------------------------------------------------------------------------

const StoreBilling: React.FC = () => {
  const scope = STORE_SCOPE;
  const { user, userInfoSettled } = useAuth();
  const periodsQ = useBillingPeriods(scope);
  const periods = useMemo(() => periodsQ.data ?? [], [periodsQ.data]);
  const p = useBillingParams(periods, STORE_RANGES);
  const { period } = p;

  const usageQ = useUsageSummary<UsageSummary>(scope, period);
  const subQ = useSubscription(scope);
  const usage = usageQ.data;
  const tz = usage?.store_timezone ?? 'UTC';
  const historyQ = useUsageHistory(
    scope,
    periods,
    period,
    p.range,
    usage?.store_timezone,
  );

  const managed = usage?.billing_managed_at_group_level === true;
  const invoicesQ = useInvoices(
    scope,
    p.page,
    periodsQ.status === 'success' &&
      (!period || usage?.billing_managed_at_group_level === false),
  );
  const invoicesGate: GateQuery<InvoicePage> =
    usageQ.status === 'error'
      ? { ...toGate(usageQ), data: undefined }
      : periodsQ.status === 'error'
        ? { ...toGate(periodsQ), data: undefined }
        : toGate(invoicesQ);
  const sub: Subscription | null =
    subQ.status === 'success'
      ? (subQ.data ?? null)
      : (usage?.subscription ?? null);
  const notStarted = isNotStarted(sub);

  const mutationsAllowed =
    userInfoSettled &&
    usageQ.status === 'success' &&
    usage?.billing_managed_at_group_level === false;
  const canManageGroup =
    userInfoSettled && user?.can_manage_group_billing === true;
  const groupId = usage?.store_group_id ?? user?.store_group_id ?? null;
  const groupName =
    user?.manageable_billing_groups?.find((g) => g.id === groupId)?.name ??
    user?.store_group_name ??
    null;
  const groupHref =
    canManageGroup && groupId != null ? groupBillingPath(groupId) : null;
  const plansHref = `${BILLING_PATH}/plans`;
  const portal = usePortal(scope);

  const { refetch: refetchUsage } = usageQ;
  const { refetch: refetchSub } = subQ;
  const { refetch: refetchPeriods } = periodsQ;
  const refetchAll = useCallback(
    () => Promise.all([refetchUsage(), refetchSub(), refetchPeriods()]),
    [refetchUsage, refetchSub, refetchPeriods],
  );
  const syncing = useStoreCheckoutRefresh(true, refetchAll);
  const includedRef = usage?.included_minutes ?? 0;
  const daily = p.range === 'week' || p.range === 'period';
  const groupWord = groupName ?? 'your store group';

  return (
    <>
      <PageMeta
        title="AskArthur | Billing"
        description="Usage, plan and invoices"
      />
      <Page
        fill
        title="Billing"
        meta={
          user?.store_name ? (
            <span className="hidden sm:inline">{user.store_name}</span>
          ) : undefined
        }
      >
        <Surface className="flex min-h-0 flex-1 flex-col">
          {period && (
            <PeriodToolbar
              periods={periods}
              periodIdx={p.periodIdx}
              defaultIdx={p.defaultIdx}
              onPeriod={p.setPeriod}
              tz={tz}
            />
          )}

          <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">
            <div className="max-w-[1160px] px-4 pb-12 pt-5 lg:px-6">
              <Notices
                syncing={syncing}
                sub={sub}
                loaded={
                  subQ.status === 'success' && usageQ.status === 'success'
                }
                tz={tz}
                managed={managed}
                mutationsAllowed={mutationsAllowed}
                portal={portal}
                plansHref={plansHref}
                groupHref={groupHref}
                groupName={groupWord}
                noun="This store"
              />

              <PeriodGate query={toGate(periodsQ)} period={period}>
                {(period) => (
                  <>
                    <section>
                      <SectionHead
                        title="This period"
                        description={
                          usage && managed
                            ? `${periodLabel(period, tz)}. This store’s minutes count toward ${groupWord}’s shared pool; there are no separate charges.`
                            : usage && sub && sub.status !== 'CANCELED'
                              ? `${periodLabel(period, tz)} in ${tzWords(tz)}. After the included minutes, calls are billed per minute at the plan’s overage rate.`
                              : usage
                                ? `${periodLabel(period, tz)} in ${tzWords(tz)}.`
                                : periodLabel(period, tz)
                        }
                      />
                      <div className="mt-3">
                        <QueryGate
                          query={toGate(usageQ)}
                          errorTitle="Could not load billing data for this period."
                          skeleton={
                            <>
                              <FiguresSummary columns={4}>
                                {Array.from({ length: 4 }).map((_, i) => (
                                  <SummaryFigureSkeleton key={i} />
                                ))}
                              </FiguresSummary>
                              <AllowanceMeterSkeleton className="mt-5 max-w-[760px]" />
                            </>
                          }
                        >
                          {(u) => (
                            <StoreFigures
                              usage={u}
                              sub={sub}
                              period={period}
                              tz={tz}
                              managed={managed}
                              notStarted={notStarted}
                              groupName={groupWord}
                            />
                          )}
                        </QueryGate>
                      </div>
                    </section>

                    <section className="mt-10">
                      <SectionHead
                        title="Usage over time"
                        description={
                          daily
                            ? `Minutes per day${p.range === 'week' ? ', last seven days' : ''}.${includedRef > 0 ? ` ${formatMinutes(includedRef)} are included per period.` : ''}`
                            : `Minutes per billing period${includedRef > 0 ? '; the dashed line is the included minutes.' : '.'}`
                        }
                        action={
                          <Segmented
                            aria-label="Range"
                            items={STORE_RANGES.map((r) => ({
                              key: r,
                              label: RANGE_LABEL[r],
                            }))}
                            value={p.range}
                            onChange={p.setRange}
                          />
                        }
                      />
                      <div className="mt-4">
                        <History
                          query={toGate(historyQ)}
                          reference={
                            includedRef > 0 && !daily && !managed
                              ? {
                                  value: includedRef,
                                  label: `${formatMinutes(includedRef)} included`,
                                }
                              : undefined
                          }
                          emptyDescription="Minutes appear here as Arthur answers calls."
                        />
                      </div>
                    </section>
                  </>
                )}
              </PeriodGate>

              <div className="mt-10 grid gap-x-12 gap-y-8 lg:grid-cols-2">
                <QueryGate
                  query={toGate(subQ)}
                  errorTitle="The subscription could not be loaded."
                  skeleton={
                    <Block first title="Plan">
                      <BlockSkeleton rows={5} />
                    </Block>
                  }
                >
                  {(s) => (
                    <StorePlanBlock
                      sub={s}
                      usage={usage}
                      tz={tz}
                      managed={managed}
                      mutationsAllowed={mutationsAllowed}
                      groupHref={groupHref}
                      groupName={groupWord}
                      plansHref={plansHref}
                      portal={portal}
                      updated={p.updated}
                    />
                  )}
                </QueryGate>
                {period ? (
                  <QueryGate
                    query={toGate(usageQ)}
                    errorTitle="Charges could not be loaded."
                    skeleton={
                      <Block first title="Charges this period">
                        <ReceiptSkeleton />
                      </Block>
                    }
                  >
                    {(u) => (
                      <StoreChargesBlock
                        usage={u}
                        sub={sub}
                        period={period}
                        tz={tz}
                        managed={managed}
                        notStarted={notStarted}
                        groupName={groupWord}
                      />
                    )}
                  </QueryGate>
                ) : (
                  <Block first title="Charges this period">
                    <Reason>
                      Charges appear once a billing period starts.
                    </Reason>
                  </Block>
                )}
              </div>

              <section className="mt-12">
                <SectionHead
                  title="Invoices"
                  description={
                    managed
                      ? `Invoices are issued to ${groupWord}, not to this store.`
                      : 'One invoice per billing period, issued by Stripe when the period closes. Open an invoice to pay it or download a PDF.'
                  }
                />
                <div className="mt-4">
                  {managed ? (
                    <Callout tone="accent" icon={<IconReceipt size={14} />}>
                      This store has no invoices of its own.{' '}
                      {groupHref ? (
                        <Link
                          to={groupHref}
                          className="underline hover:text-v2-fg"
                        >
                          Open group invoices
                        </Link>
                      ) : (
                        'Your group administrator can see them on group billing.'
                      )}
                    </Callout>
                  ) : (
                    <InvoicesTable
                      query={invoicesGate}
                      page={p.page}
                      onPage={p.setPage}
                      tz={tz}
                      emptyDescription={
                        sub && sub.status !== 'CANCELED' && period
                          ? `Your first invoice arrives when this period closes on ${formatDate(period.period_end, tz)}.`
                          : 'Invoices appear here once a plan starts.'
                      }
                    />
                  )}
                </div>
              </section>
            </div>
          </div>
        </Surface>
      </Page>
    </>
  );
};

const StoreFigures: React.FC<{
  usage: UsageSummary;
  sub: Subscription | null;
  period: BillingPeriod;
  tz: string;
  managed: boolean;
  notStarted: boolean;
  groupName: string;
}> = ({ usage: u, sub, period, tz, managed, notStarted, groupName }) => {
  const cur = u.currency;
  if (managed) {
    return (
      <>
        <FiguresSummary columns={2} aria-label="This period">
          <SummaryFigure
            label="Minutes used"
            value={formatMinutes(u.minutes_used)}
            secondary="this store"
          />
          <SummaryFigure
            label="Group pool"
            value={
              u.group_included_minutes != null
                ? formatMinutes(u.group_included_minutes)
                : '—'
            }
            secondary={
              u.group_plan_name
                ? `${u.group_plan_name} plan, shared by every store`
                : 'shared by every store'
            }
          />
        </FiguresSummary>
        <Reason className="mt-2">
          Overage, if any, is billed once on {groupName}’s invoice.
        </Reason>
      </>
    );
  }
  if (!sub || sub.status === 'CANCELED') {
    return (
      <>
        <FiguresSummary columns={2} aria-label="This period">
          <SummaryFigure
            label="Minutes used"
            value={formatMinutes(u.minutes_used)}
            secondary="no plan"
          />
          <SummaryFigure label="Charges" value="—" secondary="no plan" />
        </FiguresSummary>
        <Reason className="mt-2">Charges appear once a plan starts.</Reason>
      </>
    );
  }
  const addonCents =
    u.projected_total_cents - u.base_price_cents - u.overage_cost_cents;
  return (
    <>
      <FiguresSummary columns={4} aria-label="This period">
        <SummaryFigure
          label="Minutes used"
          value={formatMinutes(u.minutes_used)}
          secondary={`of ${formatMinutes(u.included_minutes)} included`}
        />
        <SummaryFigure
          label="Overage"
          value={formatMinutes(u.overage_minutes)}
          secondary={`${formatRate(u.overage_rate_cents_per_minute, cur, 'min')} after ${formatMinutes(u.included_minutes)}`}
        />
        <SummaryFigure
          label="Overage charges"
          value={notStarted ? '—' : formatMoney(u.overage_cost_cents, cur)}
          secondary={period.finalized ? 'invoiced' : 'so far'}
        />
        <SummaryFigure
          label={period.finalized ? 'Invoiced total' : 'Projected total'}
          value={notStarted ? '—' : formatMoney(u.projected_total_cents, cur)}
          secondary={`base ${formatMoney(u.base_price_cents, cur)}${addonCents > 0 ? ` + add-ons ${formatMoney(addonCents, cur)}` : ''}`}
        />
      </FiguresSummary>
      <AllowanceMeter
        className="mt-5 max-w-[760px]"
        label={
          period.is_current ? 'Minutes this period' : 'Minutes in this period'
        }
        value={u.minutes_used}
        max={u.included_minutes}
        format={formatMinutes}
        hint={
          u.overage_minutes > 0
            ? `${formatMoney(u.overage_cost_cents, cur, { exact: true })} in overage${period.finalized ? '' : ' so far'}`
            : `Then ${formatRate(u.overage_rate_cents_per_minute, cur, 'min')}`
        }
      />
      {u.bucket?.finalized_at && (
        <Reason className="mt-3">
          Usage was finalized on {formatDate(u.bucket.finalized_at, tz)}
          {period.invoice_id ? (
            <>
              {' '}
              and appears on invoice{' '}
              <span className="v2-num">#{period.invoice_id}</span>.
            </>
          ) : (
            '.'
          )}
        </Reason>
      )}
    </>
  );
};

type Portal = ReturnType<typeof usePortal>;

const PortalButton: React.FC<{
  portal: Portal;
  variant?: 'primary' | 'secondary';
  children: React.ReactNode;
}> = ({ portal, variant = 'secondary', children }) => (
  <Button
    size="sm"
    variant={variant}
    trailing={<IconExternal size={12} />}
    loading={portal.busy}
    onClick={portal.open}
    title="Opens the Stripe customer portal: payment method, invoices, cancel"
  >
    {children}
  </Button>
);

const StorePlanBlock: React.FC<{
  sub: Subscription | null;
  usage: UsageSummary | undefined;
  tz: string;
  managed: boolean;
  mutationsAllowed: boolean;
  groupHref: string | null;
  groupName: string;
  plansHref: string;
  portal: Portal;
  updated: boolean;
}> = ({
  sub,
  usage,
  tz,
  managed,
  mutationsAllowed,
  groupHref,
  groupName,
  plansHref,
  portal,
  updated,
}) => {
  if (managed) {
    return (
      <Block
        first
        title="Plan"
        description="Set on the group’s shared subscription."
        control={
          groupHref ? (
            <Link to={groupHref}>
              <Button
                size="sm"
                variant="secondary"
                trailing={<IconArrowRight size={12} />}
              >
                Group billing
              </Button>
            </Link>
          ) : undefined
        }
      >
        <dl>
          <Row label="Plan">
            <PlanName
              code={usage?.group_plan_code}
              name={usage?.group_plan_name ?? 'Group plan'}
              size="sm"
            />
            <Tag className="ml-2">Group</Tag>
          </Row>
          <Row label="Shared minutes">
            {usage?.group_included_minutes != null
              ? `${formatMinutes(usage.group_included_minutes)} across every store in ${groupName}`
              : '—'}
          </Row>
          <Row label="Payment">Billed to {groupName}</Row>
        </dl>
        {!groupHref && (
          <Reason className="mt-3">
            Ask your group administrator to change the plan or payment method.
          </Reason>
        )}
      </Block>
    );
  }

  if (!sub || sub.status === 'CANCELED') {
    const word = subscriptionWord(sub, tz);
    return (
      <Block
        first
        title="Plan"
        description={
          sub
            ? 'Your subscription was cancelled. Arthur is not answering calls.'
            : 'No plan yet. Arthur answers calls once a plan starts.'
        }
        control={
          mutationsAllowed ? (
            <Link to={plansHref}>
              <Button size="sm" variant="secondary">
                Compare plans
              </Button>
            </Link>
          ) : undefined
        }
      >
        <dl>
          <Row label="Plan">
            <StatusLabel size="sm" tone={word.tone} hollow={word.hollow}>
              {word.label}
            </StatusLabel>
          </Row>
          <Row label="Monthly" empty>
            —
          </Row>
          <Row label="Included minutes" empty>
            —
          </Row>
        </dl>
      </Block>
    );
  }

  const word = subscriptionWord(sub, tz);
  const custom = sub.pricing_source === 'custom';
  const cur = sub.currency;
  const addonRows = subscriptionAddonRows(sub);
  const renewLabel =
    sub.status === 'PAST_DUE'
      ? 'Renewed'
      : sub.cancel_at_period_end
        ? 'Ends'
        : sub.status === 'TRIALING'
          ? 'Trial ends'
          : 'Renews';
  const renewDate =
    sub.status === 'PAST_DUE'
      ? sub.current_period_start
      : sub.current_period_end;

  return (
    <Block
      first
      title="Plan"
      control={
        mutationsAllowed && sub.status !== 'PAST_DUE' ? (
          <>
            <Link to={plansHref}>
              <Button size="sm" variant="secondary">
                Change plan
              </Button>
            </Link>
            <PortalButton portal={portal}>Payment &amp; invoices</PortalButton>
          </>
        ) : undefined
      }
    >
      {updated && (
        <p role="status" className="mb-2 text-v2-12 text-v2-success">
          {sub.pending_plan ? 'Plan change scheduled.' : 'Plan updated.'}
        </p>
      )}
      <dl>
        <Row label="Plan">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <PlanName
              code={sub.plan?.code}
              name={sub.plan?.name ?? 'Plan'}
              size="sm"
            />
            <StatusLabel size="sm" tone={word.tone} hollow={word.hollow}>
              {word.label}
            </StatusLabel>
            {custom && (
              <Tag tone={PLAN_TAG.custom.tone}>{PLAN_TAG.custom.label}</Tag>
            )}
          </span>
        </Row>
        <Row label="Monthly">
          <span className="v2-num">
            {formatMoney(sub.base_price_cents, cur)}/mo
          </span>
          {custom && (
            <span className="ml-2 text-v2-12 text-v2-fg-3">agreed price</span>
          )}
        </Row>
        <Row label="Included minutes">
          <span className="v2-num">{formatMinutes(sub.included_minutes)}</span>
        </Row>
        <Row label="After that">
          <span className="v2-num">
            {formatRate(sub.overage_rate_cents_per_minute, cur, 'min')}
          </span>
        </Row>
        {(sub.per_order_rate_cents ?? 0) > 0 && (
          <Row label="Confirmed orders">
            <span className="v2-num">
              {formatRate(sub.per_order_rate_cents ?? 0, cur, 'order')}
            </span>
          </Row>
        )}
        <Row label="Add-ons" empty={addonRows.length === 0}>
          {addonRows.length === 0 ? (
            'None'
          ) : (
            <ul className="flex flex-col gap-0.5">
              {addonRows.map((a) => (
                <li key={a.key} className="flex items-baseline gap-2">
                  <span>{a.label}</span>
                  <span className="v2-num text-v2-12 text-v2-fg-3">
                    {a.included ? 'Included' : formatRate(a.cents, cur, 'mo')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Row>
        {renewDate && (
          <Row label={renewLabel}>
            <span className="v2-num">{formatDate(renewDate, tz)}</span>
            {sub.status === 'PAST_DUE' && (
              <span className="ml-2 text-v2-12 text-v2-danger">
                payment failed
              </span>
            )}
          </Row>
        )}
      </dl>
      {sub.pending_plan && (
        <Callout tone="accent" className="mt-3">
          Changes to{' '}
          <span className="font-medium text-v2-fg">
            {sub.pending_plan.name}
          </span>{' '}
          on{' '}
          {sub.current_period_end
            ? formatDate(sub.current_period_end, tz)
            : 'the next period'}
          {sub.pending_base_price_cents != null && (
            <>
              {' '}
              — {formatMoney(sub.pending_base_price_cents, cur)}/mo from then
            </>
          )}
          .
        </Callout>
      )}
      {portal.error && sub.status !== 'PAST_DUE' && (
        <Callout tone="danger" className="mt-3">
          {portal.error}
        </Callout>
      )}
    </Block>
  );
};

const StoreChargesBlock: React.FC<{
  usage: UsageSummary;
  sub: Subscription | null;
  period: BillingPeriod;
  tz: string;
  managed: boolean;
  notStarted: boolean;
  groupName: string;
}> = ({ usage: u, sub, period, tz, managed, notStarted, groupName }) => {
  const title = period.finalized
    ? 'Charges for this period'
    : 'Charges this period';
  if (managed) {
    return (
      <Block first title={title}>
        <Reason>
          This store has no separate charges. The base fee and any pooled
          overage are billed once on {groupName}’s invoice
          {u.group_plan_name ? ` (${u.group_plan_name} plan)` : ''}.
        </Reason>
      </Block>
    );
  }
  if (!sub || sub.status === 'CANCELED') {
    return (
      <Block first title={title}>
        <Reason>Charges appear once a plan starts.</Reason>
      </Block>
    );
  }
  if (notStarted) {
    return (
      <Block first title={title}>
        <Reason>Charges start when the subscription is activated.</Reason>
      </Block>
    );
  }
  return (
    <Block first title={title}>
      <ReceiptLines lines={storeChargeLines(u)} />
      <ReceiptTotal
        className="mt-1"
        label={period.finalized ? 'Invoiced total' : 'Projected total'}
        value={formatMoney(u.projected_total_cents, u.currency, {
          exact: true,
        })}
        note={
          period.finalized
            ? 'Invoiced. The invoice is listed below.'
            : `Billed on ${formatDate(period.period_end, tz)}. Overage grows with usage until then; tax is added by Stripe.`
        }
      />
    </Block>
  );
};

// ---------------------------------------------------------------------------
// Group scope
// ---------------------------------------------------------------------------

const GroupBilling: React.FC = () => {
  const { groupId = '' } = useParams();
  const scope = useMemo<BillingScope>(
    () => ({ kind: 'group', groupId }),
    [groupId],
  );
  const navigate = useNavigate();
  const { user } = useAuth();
  const groups = user?.manageable_billing_groups ?? [];

  const periodsQ = useBillingPeriods(scope);
  const periods = useMemo(() => periodsQ.data ?? [], [periodsQ.data]);
  const p = useBillingParams(periods, GROUP_RANGES);
  const { period } = p;
  const subQ = useSubscription(scope);
  const usageQ = useUsageSummary<GroupUsageSummary>(scope, period);
  const usage = usageQ.data;
  const tz = usage?.billing_timezone ?? 'UTC';
  const historyQ = useUsageHistory(scope, periods, period, p.range, tz);
  const invoicesQ = useInvoices(scope, p.page);
  const portal = usePortal(scope);

  const { refetch: refetchUsage } = usageQ;
  const { refetch: refetchSub } = subQ;
  const { refetch: refetchPeriods } = periodsQ;
  const { refetch: refetchInvoices } = invoicesQ;
  const refetchAll = useCallback(
    () =>
      Promise.all([
        refetchUsage(),
        refetchSub(),
        refetchPeriods(),
        refetchInvoices(),
      ]),
    [refetchUsage, refetchSub, refetchPeriods, refetchInvoices],
  );
  const syncing = useGroupCheckoutRefresh(groupId, refetchAll);

  const rawSub = subQ.data ?? null;
  const sub = rawSub && rawSub.status !== 'CANCELED' ? rawSub : null;
  const name =
    usage?.group_name ??
    groups.find((g) => String(g.id) === groupId)?.name ??
    'Group billing';
  const plansHref = `${groupBillingPath(groupId)}/plans`;
  const includedRef = usage?.included_minutes ?? 0;
  const word = subscriptionWord(sub, tz);

  return (
    <>
      <PageMeta
        title={`AskArthur | ${name} billing`}
        description="Shared plan and pooled usage across the group"
      />
      <Page
        fill
        title={name}
        meta={<span className="hidden sm:inline">Group billing</span>}
      >
        <Surface className="flex min-h-0 flex-1 flex-col">
          {(period || groups.length > 1) && (
            <PeriodToolbar
              periods={periods}
              periodIdx={p.periodIdx}
              defaultIdx={p.defaultIdx}
              onPeriod={p.setPeriod}
              tz={tz}
              leading={
                groups.length > 1 ? (
                  <Select
                    size="sm"
                    aria-label="Select store group"
                    value={groupId}
                    onChange={(e) => navigate(groupBillingPath(e.target.value))}
                    className="w-[168px]"
                  >
                    {groups.map((g) => (
                      <option key={g.id} value={String(g.id)}>
                        {g.name}
                      </option>
                    ))}
                  </Select>
                ) : undefined
              }
            />
          )}

          <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">
            <div className="max-w-[1160px] px-4 pb-12 pt-5 lg:px-6">
              <Notices
                syncing={syncing}
                sub={rawSub}
                loaded={subQ.status === 'success'}
                tz={tz}
                managed={false}
                mutationsAllowed
                portal={portal}
                plansHref={plansHref}
                groupHref={null}
                groupName={name}
                noun={name}
                group
              />

              <PeriodGate query={toGate(periodsQ)} period={period}>
                {(period) => (
                  <>
                    <section>
                      <SectionHead
                        title="This period"
                        description={`${periodLabel(period, tz)} in ${tzWords(tz)}. Every store’s minutes draw on one shared pool; overage is split by each store’s share.`}
                      />
                      <div className="mt-3">
                        <QueryGate
                          query={toGate(usageQ)}
                          errorTitle="Group usage could not be loaded."
                          skeleton={
                            <FiguresSummary columns={4}>
                              {Array.from({ length: 4 }).map((_, i) => (
                                <SummaryFigureSkeleton key={i} />
                              ))}
                            </FiguresSummary>
                          }
                        >
                          {(g) => <GroupFigures usage={g} hasPlan={!!sub} />}
                        </QueryGate>
                      </div>
                    </section>

                    <section className="mt-10">
                      <QueryGate
                        query={toGate(usageQ)}
                        errorTitle="Group usage could not be loaded."
                        skeleton={
                          <>
                            <SectionHead
                              title="By store"
                              description="Each store’s share of the pool."
                            />
                            <div className="mt-3 max-w-[760px]">
                              <BreakdownSkeleton />
                            </div>
                          </>
                        }
                      >
                        {(g) => <ByStore usage={g} />}
                      </QueryGate>
                    </section>

                    <section className="mt-10">
                      <SectionHead
                        title="Usage over time"
                        description={`Shared minutes per billing period${includedRef > 0 ? '; the dashed line is the pool.' : '.'}`}
                        action={
                          <Segmented
                            aria-label="Range"
                            items={GROUP_RANGES.map((r) => ({
                              key: r,
                              label: RANGE_LABEL[r],
                            }))}
                            value={p.range}
                            onChange={p.setRange}
                          />
                        }
                      />
                      <div className="mt-4">
                        <History
                          query={toGate(historyQ)}
                          reference={
                            includedRef > 0
                              ? {
                                  value: includedRef,
                                  label: `${formatMinutes(includedRef)} shared`,
                                }
                              : undefined
                          }
                          emptyDescription="Minutes appear here as Arthur answers calls at any store."
                        />
                      </div>
                    </section>
                  </>
                )}
              </PeriodGate>

              <div className="mt-10 grid gap-x-12 gap-y-8 lg:grid-cols-2">
                <QueryGate
                  query={toGate(subQ)}
                  errorTitle="The shared subscription could not be loaded."
                  skeleton={
                    <Block first title="Shared plan">
                      <BlockSkeleton rows={5} />
                    </Block>
                  }
                >
                  {() =>
                    !sub ? (
                      <Block
                        first
                        title="Shared plan"
                        description="No shared subscription yet. Each store is billed on its own until one starts."
                        control={
                          <Link to={plansHref}>
                            <Button size="sm" variant="secondary">
                              Compare plans
                            </Button>
                          </Link>
                        }
                      >
                        <dl>
                          <Row label="Plan">
                            <StatusLabel size="sm" tone="neutral" hollow>
                              {rawSub ? 'Cancelled' : 'No plan'}
                            </StatusLabel>
                          </Row>
                          <Row label="Stores">
                            <span className="v2-num">
                              {usage?.stores.length ?? '—'}
                            </span>
                          </Row>
                        </dl>
                      </Block>
                    ) : (
                      <Block
                        first
                        title="Shared plan"
                        control={
                          sub.status === 'PAST_DUE' ? undefined : (
                            <>
                              <Link to={plansHref}>
                                <Button size="sm" variant="secondary">
                                  Change plan
                                </Button>
                              </Link>
                              <PortalButton portal={portal}>
                                Payment &amp; invoices
                              </PortalButton>
                            </>
                          )
                        }
                      >
                        {p.updated && (
                          <p
                            role="status"
                            className="mb-2 text-v2-12 text-v2-success"
                          >
                            {sub.pending_plan
                              ? 'Plan change scheduled.'
                              : 'Plan updated.'}
                          </p>
                        )}
                        <dl>
                          <Row label="Plan">
                            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <PlanName
                                code={sub.plan?.code}
                                name={sub.plan?.name ?? 'Plan'}
                                size="sm"
                              />
                              <StatusLabel
                                size="sm"
                                tone={word.tone}
                                hollow={word.hollow}
                              >
                                {word.label}
                              </StatusLabel>
                              <Tag>Group</Tag>
                            </span>
                          </Row>
                          <Row label="Monthly">
                            <span className="v2-num">
                              {formatMoney(sub.base_price_cents, sub.currency)}
                              /mo
                            </span>
                          </Row>
                          <Row label="Shared minutes">
                            <span className="v2-num">
                              {formatMinutes(sub.included_minutes)}
                            </span>
                            {usage && (
                              <span className="ml-2 text-v2-12 text-v2-fg-3">
                                across {usage.stores.length} stores
                              </span>
                            )}
                          </Row>
                          <Row label="After that">
                            <span className="v2-num">
                              {formatRate(
                                sub.overage_rate_cents_per_minute,
                                sub.currency,
                                'min',
                              )}
                            </span>
                            <span className="ml-2 text-v2-12 text-v2-fg-3">
                              split by share
                            </span>
                          </Row>
                          {sub.current_period_end && (
                            <Row
                              label={
                                sub.status === 'PAST_DUE'
                                  ? 'Renewed'
                                  : sub.cancel_at_period_end
                                    ? 'Ends'
                                    : 'Renews'
                              }
                            >
                              <span className="v2-num">
                                {formatDate(
                                  sub.status === 'PAST_DUE' &&
                                    sub.current_period_start
                                    ? sub.current_period_start
                                    : sub.current_period_end,
                                  tz,
                                )}
                              </span>
                            </Row>
                          )}
                        </dl>
                        {sub.pending_plan && (
                          <Callout tone="accent" className="mt-3">
                            Changes to{' '}
                            <span className="font-medium text-v2-fg">
                              {sub.pending_plan.name}
                            </span>{' '}
                            on{' '}
                            {sub.current_period_end
                              ? formatDate(sub.current_period_end, tz)
                              : 'the next period'}
                            .
                          </Callout>
                        )}
                        {portal.error && sub.status !== 'PAST_DUE' && (
                          <Callout tone="danger" className="mt-3">
                            {portal.error}
                          </Callout>
                        )}
                      </Block>
                    )
                  }
                </QueryGate>
                {period ? (
                  <QueryGate
                    query={toGate(usageQ)}
                    errorTitle="Charges could not be loaded."
                    skeleton={
                      <Block first title="Charges this period">
                        <ReceiptSkeleton />
                      </Block>
                    }
                  >
                    {(g) =>
                      !sub ? (
                        <Block first title="Charges this period">
                          <Reason>
                            Group charges appear once a shared plan starts.
                          </Reason>
                        </Block>
                      ) : (
                        <Block
                          first
                          title={
                            g.allocations_frozen
                              ? 'Charges for this period'
                              : 'Charges this period'
                          }
                        >
                          <ReceiptLines lines={groupChargeLines(g)} />
                          <ReceiptTotal
                            className="mt-1"
                            label={
                              g.allocations_frozen
                                ? 'Invoiced total'
                                : 'Projected total'
                            }
                            value={formatMoney(
                              g.projected_total_cents,
                              g.currency,
                              {
                                exact: true,
                              },
                            )}
                            note={
                              g.allocations_frozen
                                ? 'One invoice to the group; the per-store split is final.'
                                : `Billed on ${formatDate(period.period_end, tz)} as one group invoice. Tax is added by Stripe.`
                            }
                          />
                        </Block>
                      )
                    }
                  </QueryGate>
                ) : (
                  <Block first title="Charges this period">
                    <Reason>
                      Charges appear once a billing period starts.
                    </Reason>
                  </Block>
                )}
              </div>

              <section className="mt-12">
                <SectionHead
                  title="Invoices"
                  description="One invoice per period for the whole group."
                />
                <div className="mt-4">
                  <InvoicesTable
                    query={toGate(invoicesQ)}
                    page={p.page}
                    onPage={p.setPage}
                    tz={tz}
                    emptyDescription={
                      sub && period
                        ? `The first group invoice arrives when this period closes on ${formatDate(period.period_end, tz)}.`
                        : 'Group invoices appear once a shared plan starts.'
                    }
                  />
                </div>
              </section>
            </div>
          </div>
        </Surface>
      </Page>
    </>
  );
};

const GroupFigures: React.FC<{
  usage: GroupUsageSummary;
  hasPlan: boolean;
}> = ({ usage: g, hasPlan }) => {
  if (!hasPlan) {
    return (
      <>
        <FiguresSummary columns={2} aria-label="This period">
          <SummaryFigure
            label="Minutes used"
            value={formatMinutes(g.total_minutes_used)}
            secondary={`across ${g.stores.length} stores`}
          />
          <SummaryFigure label="Charges" value="—" secondary="no shared plan" />
        </FiguresSummary>
        <Reason className="mt-2">
          Stores in this group are billed separately until a shared plan starts.
        </Reason>
      </>
    );
  }
  return (
    <>
      <FiguresSummary columns={4} aria-label="This period">
        <SummaryFigure
          label="Minutes used"
          value={formatMinutes(g.total_minutes_used)}
          secondary={`of ${formatMinutes(g.included_minutes)} shared`}
        />
        <SummaryFigure
          label="Overage"
          value={formatMinutes(g.overage_minutes)}
          secondary={`${formatRate(g.overage_rate_cents_per_minute, g.currency, 'min')} after ${formatMinutes(g.included_minutes)}`}
        />
        <SummaryFigure
          label="Overage charges"
          value={formatMoney(g.overage_cost_cents, g.currency)}
          secondary={g.allocations_frozen ? 'invoiced' : 'so far, pooled'}
        />
        <SummaryFigure
          label={g.allocations_frozen ? 'Invoiced total' : 'Projected total'}
          value={formatMoney(g.projected_total_cents, g.currency)}
          secondary={`base ${formatMoney(g.base_price_cents, g.currency)} + add-ons ${formatMoney(g.addon_cost_cents, g.currency)}`}
        />
      </FiguresSummary>
      <AllowanceMeter
        className="mt-5 max-w-[760px]"
        label="Shared minutes this period"
        value={g.total_minutes_used}
        max={g.included_minutes}
        format={formatMinutes}
        hint={
          g.overage_minutes > 0
            ? `${formatMoney(g.overage_cost_cents, g.currency, { exact: true })} in pooled overage`
            : `Then ${formatRate(g.overage_rate_cents_per_minute, g.currency, 'min')}`
        }
      />
    </>
  );
};

const ByStore: React.FC<{ usage: GroupUsageSummary }> = ({ usage: g }) => (
  <>
    <SectionHead
      title="By store"
      description="Each store’s share of the shared minutes, and its share of any overage."
      action={
        <StatusLabel
          size="sm"
          tone={g.allocations_frozen ? 'success' : 'accent'}
          hollow={!g.allocations_frozen}
        >
          {g.allocations_frozen ? 'Invoiced split' : 'Live projection'}
        </StatusLabel>
      }
    />
    {g.stores.length === 0 ? (
      <div className="mt-3 border-y border-v2-line">
        <EmptyState compact title="No stores in this group yet" />
      </div>
    ) : (
      <Breakdown
        className="mt-2 max-w-[760px]"
        palette="multi"
        noun="minutes"
        total={g.total_minutes_used || undefined}
        rows={g.stores.map((s) => ({
          key: String(s.store_id),
          label: s.store_name,
          value: Math.round(s.minutes_used),
          tone: hueFor(s.store_id),
          sub:
            g.overage_cost_cents > 0
              ? `${formatMoney(s.allocated_overage_cents, g.currency, { exact: true })} of the overage`
              : undefined,
        }))}
        aria-label="Minutes by store"
      />
    )}
  </>
);

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/** Second header row: ‹ period › · period state · (group select) · timezone. */
const PeriodToolbar: React.FC<{
  periods: BillingPeriod[];
  periodIdx: number;
  defaultIdx: number;
  onPeriod: (i: number) => void;
  tz: string;
  leading?: React.ReactNode;
}> = ({ periods, periodIdx, defaultIdx, onPeriod, tz, leading }) => {
  const p = periods[periodIdx];
  return (
    <div className="flex h-11 items-center gap-3 border-b border-v2-line px-4 lg:px-6">
      {leading}
      {p && (
        <>
          <PeriodNav
            label={periodLabel(p, tz)}
            onPrev={() => onPeriod(periodIdx + 1)}
            onNext={() => onPeriod(periodIdx - 1)}
            hasPrev={periodIdx < periods.length - 1}
            hasNext={periodIdx > 0}
            onCurrent={() => onPeriod(defaultIdx)}
            isCurrent={periodIdx === defaultIdx}
            prevLabel="Older period"
            nextLabel="Newer period"
          />
          <StatusLabel
            size="sm"
            tone={p.finalized ? 'success' : 'accent'}
            hollow={!p.finalized}
            className="hidden sm:inline-flex"
          >
            {p.is_current
              ? 'In progress'
              : p.finalized
                ? 'Invoiced'
                : 'Closing'}
          </StatusLabel>
          <span className="ml-auto hidden text-v2-12 text-v2-fg-3 md:inline">
            Times in {tzWords(tz)}
          </span>
        </>
      )}
    </div>
  );
};

/** The period-bound sections: skeleton, error, "no periods yet", or content. */
const PeriodGate: React.FC<{
  query: GateQuery<BillingPeriod[]>;
  period: BillingPeriod | undefined;
  children: (period: BillingPeriod) => React.ReactNode;
}> = ({ query, period, children }) => (
  <QueryGate
    query={query}
    errorTitle="Billing periods could not be loaded."
    skeleton={
      <>
        <FiguresSummary columns={4}>
          {Array.from({ length: 4 }).map((_, i) => (
            <SummaryFigureSkeleton key={i} />
          ))}
        </FiguresSummary>
        <AllowanceMeterSkeleton className="mt-5 max-w-[760px]" />
      </>
    }
  >
    {() =>
      period ? (
        children(period)
      ) : (
        <div className="border-y border-v2-line">
          <EmptyState
            compact
            title="No billing periods yet"
            description="Usage and charges appear here once the first billing period starts."
          />
        </div>
      )
    }
  </QueryGate>
);

const History: React.FC<{
  query: GateQuery<Bar[]>;
  reference?: { value: number; label: string };
  emptyDescription: string;
}> = ({ query, reference, emptyDescription }) => (
  <QueryGate
    query={query}
    errorTitle="Usage history could not be loaded."
    skeleton={<BarsSkeleton />}
  >
    {(bars) =>
      bars.every((b) => b.value === 0) ? (
        <div className="border-y border-v2-line">
          <EmptyState
            compact
            title="No calls in this range"
            description={emptyDescription}
          />
        </div>
      ) : (
        <Bars
          bars={bars}
          format={formatMinutes}
          reference={reference}
          aria-label="Minutes used over time"
        />
      )
    }
  </QueryGate>
);

/**
 * At most one callout at the top of the page, in priority order:
 * syncing → past due (carries the primary) → not started → ends → no plan
 * → managed.
 */
const Notices: React.FC<{
  syncing: boolean;
  sub: Subscription | null;
  /** True once the reads settled, so "no plan" isn't shown while loading. */
  loaded: boolean;
  tz: string;
  managed: boolean;
  mutationsAllowed: boolean;
  portal: Portal;
  plansHref: string;
  groupHref: string | null;
  groupName: string;
  /** "This store" or the group name, for the no-plan sentence. */
  noun: string;
  group?: boolean;
}> = ({
  syncing,
  sub,
  loaded,
  tz,
  managed,
  mutationsAllowed,
  portal,
  plansHref,
  groupHref,
  groupName,
  noun,
  group,
}) => {
  if (syncing) {
    return (
      <Callout
        tone="accent"
        icon={<Spinner className="text-v2-accent" />}
        title={
          group
            ? 'Syncing your group subscription with Stripe…'
            : 'Confirming your subscription with Stripe…'
        }
        className="mb-6"
      >
        This usually takes a few seconds. The plan and invoices below update on
        their own.
      </Callout>
    );
  }
  if (managed) {
    return (
      <Callout tone="accent" title="Billed at the group level" className="mb-6">
        Billing for this store is managed by your store group, {groupName}. The
        plan, payment method and invoices live on the group subscription; this
        store has no separate charges.
        {groupHref ? (
          <div className="mt-2">
            <Link
              to={groupHref}
              className="inline-flex items-center gap-1 font-medium text-v2-fg underline hover:text-v2-accent"
            >
              Open group billing <IconArrowRight size={12} />
            </Link>
          </div>
        ) : (
          <div className="mt-1 text-v2-fg-3">
            Ask your group administrator to make changes.
          </div>
        )}
      </Callout>
    );
  }
  if (sub?.status === 'PAST_DUE') {
    return (
      <Callout
        tone="danger"
        title="Payment failed — Arthur keeps answering for now."
        className="mb-6"
      >
        The latest invoice is unpaid. Update the payment method to keep your
        plan; Stripe retries automatically.
        {mutationsAllowed && (
          <div className="mt-2.5 flex items-center gap-3">
            <PortalButton portal={portal} variant="primary">
              Update payment method
            </PortalButton>
            {portal.error && (
              <span className="text-v2-12 text-v2-danger">{portal.error}</span>
            )}
          </div>
        )}
      </Callout>
    );
  }
  if (isNotStarted(sub)) {
    return (
      <Callout
        tone="neutral"
        title="Your subscription hasn’t started yet."
        className="mb-6"
      >
        Plan details and charges become active on the billing start date agreed
        at activation.
      </Callout>
    );
  }
  if (sub?.cancel_at_period_end && sub.current_period_end) {
    return (
      <Callout
        tone="warning"
        title={`Your plan ends on ${formatDate(sub.current_period_end, tz)}.`}
        className="mb-6"
      >
        Arthur stops answering calls after that. You can undo the cancellation
        in the Stripe portal until then.
        {mutationsAllowed && (
          <div className="mt-2.5">
            <PortalButton portal={portal}>Keep my plan</PortalButton>
          </div>
        )}
      </Callout>
    );
  }
  if (loaded && (!sub || sub.status === 'CANCELED')) {
    return (
      <Callout
        tone="accent"
        title={
          sub
            ? `${noun}’s plan was cancelled.`
            : group
              ? `${noun} has no shared plan yet.`
              : `${noun} has no plan yet.`
        }
        className="mb-6"
      >
        {group
          ? 'Each store is billed on its own until a shared plan starts. One plan pools minutes across every store and sends one invoice.'
          : 'Arthur isn’t answering calls until a plan starts. Choosing one takes a minute; payment is on Stripe.'}
        {mutationsAllowed && (
          <div className="mt-2.5">
            <Link to={plansHref}>
              <Button
                size="sm"
                variant="primary"
                trailing={<IconArrowRight size={12} />}
              >
                {group ? 'Choose a shared plan' : 'Choose a plan'}
              </Button>
            </Link>
          </div>
        )}
      </Callout>
    );
  }
  return null;
};

const InvoicesTable: React.FC<{
  query: GateQuery<InvoicePage>;
  page: number;
  onPage: (p: number) => void;
  tz: string;
  emptyDescription: string;
}> = ({ query, page, onPage, tz, emptyDescription }) => (
  <div className="-mx-4 lg:-mx-6">
    <QueryGate
      query={query}
      errorTitle="Invoices could not be loaded."
      skeleton={
        <Table fixed>
          <InvoicesHead />
          <TBody>
            <TableSkeletonRows rows={4} cols={4} />
          </TBody>
        </Table>
      }
    >
      {({ results, count }) =>
        results.length === 0 ? (
          <div className="border-y border-v2-line">
            <EmptyState
              compact
              icon={<IconReceipt />}
              title="No invoices yet"
              description={emptyDescription}
            />
          </div>
        ) : (
          <>
            <Table fixed>
              <InvoicesHead />
              <TBody>
                {results.map((inv) => {
                  const w = invoiceWord(inv.status);
                  const pay =
                    inv.status === 'OPEN' || inv.status === 'UNCOLLECTIBLE';
                  return (
                    <TR key={inv.id}>
                      <TD num>{invoicePeriodLabel(inv, tz)}</TD>
                      <TD>
                        <StatusLabel size="sm" tone={w.tone} hollow={w.hollow}>
                          {w.label}
                        </StatusLabel>
                      </TD>
                      <TD
                        num
                        align="right"
                        className={clsx(
                          inv.status === 'VOID' && 'text-v2-fg-3 line-through',
                        )}
                      >
                        {formatMoney(inv.total_cents, inv.currency, {
                          exact: true,
                        })}
                      </TD>
                      <TD align="right">
                        {inv.hosted_invoice_url && inv.status !== 'DRAFT' ? (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-v2-fg-2 hover:text-v2-accent"
                          >
                            {pay ? 'Pay' : 'View'} <IconExternal size={11} />
                          </a>
                        ) : (
                          <span className="text-v2-fg-4">—</span>
                        )}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
            {count > INVOICES_PER_PAGE && (
              <Pagination
                page={page}
                totalPages={Math.ceil(count / INVOICES_PER_PAGE)}
                totalCount={count}
                pageSize={INVOICES_PER_PAGE}
                onPage={onPage}
                noun="invoices"
              />
            )}
          </>
        )
      }
    </QueryGate>
  </div>
);

const InvoicesHead: React.FC = () => (
  <THead>
    <tr>
      <TH className="w-[220px]">Period</TH>
      <TH>Status</TH>
      <TH className="w-[120px]" align="right">
        Amount
      </TH>
      <TH className="w-[88px]" align="right">
        Invoice
      </TH>
    </tr>
  </THead>
);
